import { useEffect, useState } from "react";
import axios from "axios";
import { base_url } from "../context/Authcontext";

/* =====================================================================
   Support contacts
   =====================================================================
   The WhatsApp number and Telegram link live in the admin_tele table and
   are edited in Admin -> Accounts. Every "support" surface on the site
   reads them from here -- the floating buttons, the Contact Us page, the
   header, the sign-in help panel -- so changing them in the admin panel
   changes them everywhere, and nothing is hardcoded.

   One request per page load, shared (same pattern as useBanners): the
   floating button, the header and a page can all mount at once.
   ===================================================================== */

let cache = null;
let inFlight = null;
const listeners = new Set();

const EMPTY = { whatsapp: "", telegram: "" };

/* Row -> the two things the UI needs. The number is stored as typed in
   the admin form ("+91 83768 68895"); wa.me wants digits only. Telegram
   is offered whenever a link is set, unless the service is switched off. */
const normalise = (row = {}) => {
  const whatsapp = String(row.whatsappnumber || "").replace(/[^0-9]/g, "");
  let telegram = "";
  // Shown whenever a link is set, unless the admin explicitly turned the
  // service off. Admin -> Settings labels `teleservice` "Customer Service
  // Link" and asks for a URL, so requiring the literal "yes" hid Telegram
  // from every player as soon as a link was typed there.
  const off = /^(no|off|false|0|disabled?)$/i.test(String(row.teleservice || "").trim());
  if (!off && row.telelink) {
    const raw = String(row.telelink).trim();
    // Accept "@handle" and "t.me/handle" as well as a full URL.
    if (/^https?:\/\//i.test(raw)) telegram = raw;
    else if (raw.startsWith("@")) telegram = `https://t.me/${raw.slice(1)}`;
    else if (raw) telegram = `https://${raw.replace(/^\/+/, "")}`;
  }
  return { whatsapp, telegram };
};

const load = () => {
  if (cache) return Promise.resolve(cache);
  if (inFlight) return inFlight;
  inFlight = axios
    .get(`${base_url}/admin/webapi/get/telegramData`)
    .then((res) => {
      cache = res.data && res.data.success ? normalise(res.data.result?.[0]) : EMPTY;
      return cache;
    })
    .catch((err) => {
      console.error("Error fetching support contacts:", err);
      cache = EMPTY;
      return cache;
    })
    .finally(() => {
      inFlight = null;
      listeners.forEach((fn) => fn(cache));
    });
  return inFlight;
};

export const whatsappUrl = (number, message = "Hello! I need support.") =>
  number ? `https://wa.me/${number}?text=${encodeURIComponent(message)}` : "";

/* Returns null while loading, then { whatsapp, telegram } (either may be
   "" when the admin has not configured it). */
export const useSupportContacts = () => {
  const [contacts, setContacts] = useState(cache);

  useEffect(() => {
    listeners.add(setContacts);
    if (!cache) load();
    return () => listeners.delete(setContacts);
  }, []);

  return contacts;
};
